// Pattern-based blocks
const BLOCK_PATTERNS = [
  /^www\d*$/, // www, www1, www2
  /^(localhost|local|127-0-0-1|::1)$/, // localhost variations
  /^\d{1,3}(-\d{1,3}){3}$/, // IP-like (e.g., 192-168-0-1)
  /^10-\d{1,3}(-\d{1,3}){2}$/, // private IP range 10.x
  /^192-168(-\d{1,3}){2}$/, // private IP range 192.168.x
  /^172-(1[6-9]|2\d|3[0-1])(-\d{1,3}){2}$/ // private IP range 172.16-31.x
];

// Infrastructure reserved - these subdomains should never route to restaurants
// Used by the proxy to protect actual services (app.domain.com, api.domain.com, etc.)
const INFRASTRUCTURE_RESERVED = new Set([
  'account',
  'accounts',
  'admin',
  'api',
  'app',
  'assets',
  'auth',
  'autoconfig',
  'autodiscover',
  'billing',
  'blog',
  'bounce',
  'careers',
  'cdn',
  'chat',
  'community',
  'console',
  'dashboard',
  'developer',
  'discuss',
  'dns',
  'docs',
  'download',
  'email',
  'feedback',
  'files',
  'forum',
  'ftp',
  'go',
  'health',
  'help',
  'imap',
  'img',
  'images',
  'internal',
  'intranet',
  'investors',
  'jobs',
  'l',
  'legal',
  'link',
  'login',
  'm',
  'mail',
  'mailgun',
  'media',
  'metrics',
  'monitor',
  'mx',
  'news',
  'newsroom',
  'newsletter',
  'noreply',
  'ns',
  'ns1',
  'ns2',
  'ns3',
  'oauth',
  'owa',
  'partners',
  'payments',
  'pop',
  'postmark',
  'press',
  'privacy',
  'profile',
  'r',
  'redirect',
  'refunds',
  'root',
  'sendgrid',
  'settings',
  'sftp',
  'signin',
  'signup',
  'smtp',
  'sparkpost',
  'ssh',
  'sso',
  'staff',
  'static',
  'status',
  'storage',
  'support',
  'survey',
  't',
  'terms',
  'track',
  'unsubscribe',
  'uploads',
  'uptime',
  'user',
  'users',
  'ww',
  'ww1',
  'ww2',
  'www'
]);

// Reserved words for database validation - users cannot create restaurants with these slugs
// Includes infrastructure + demo/test slugs (which can exist but only created by admins)
// !! IMPORTANT: Keep in sync with supabase/schemas/functions/validate_restaurant_slug.sql
const RESERVED_WORDS = new Set([
  ...INFRASTRUCTURE_RESERVED,
  // Demo/test slugs - can exist as restaurants but users can't create them
  'concealed',
  'demo',
  'dev',
  'preprod',
  'prod',
  'sandbox',
  'staging',
  'test',
  'testing'
]);

const SLUG_ERROR_CODES = {
  ALREADY_TAKEN: 'already_taken',
  CONSECUTIVE_DASHES: 'consecutive_dashes',
  INVALID_CHARACTERS: 'invalid_characters',
  INVALID_LENGTH: 'invalid_length',
  INVALID_SHAPE: 'invalid_shape',
  RESERVED: 'reserved'
};

const validateSlugFormat = (slug) => {
  if (!slug || typeof slug !== 'string') {
    return {
      errorCode: SLUG_ERROR_CODES.INVALID_CHARACTERS,
      isValid: false
    };
  }

  const normalizedSlug = slug.toLowerCase().trim();

  // Must be alphanumeric or dash only
  if (!/^[a-z0-9-]+$/.test(normalizedSlug)) {
    return {
      errorCode: SLUG_ERROR_CODES.INVALID_CHARACTERS,
      isValid: false
    };
  }

  // Reasonable length
  if (normalizedSlug.length < 3 || normalizedSlug.length > 30) {
    return {
      errorCode: SLUG_ERROR_CODES.INVALID_LENGTH,
      isValid: false
    };
  }

  // Shape rules
  if (normalizedSlug.startsWith('-') || normalizedSlug.endsWith('-')) {
    return {
      errorCode: SLUG_ERROR_CODES.INVALID_SHAPE,
      isValid: false
    };
  }

  if (normalizedSlug.includes('--')) {
    return {
      errorCode: SLUG_ERROR_CODES.CONSECUTIVE_DASHES,
      isValid: false
    };
  }

  // Reserved words (infrastructure + demo/test)
  if (RESERVED_WORDS.has(normalizedSlug)) {
    return {
      errorCode: SLUG_ERROR_CODES.RESERVED,
      isValid: false
    };
  }

  // Pattern-based blocks
  for (const pattern of BLOCK_PATTERNS) {
    if (pattern.test(normalizedSlug)) {
      return {
        errorCode: SLUG_ERROR_CODES.RESERVED,
        isValid: false
      };
    }
  }

  return {
    isValid: true
  };
};

const getIsRestaurantSlugValid = (subdomain) => {
  if (!subdomain || typeof subdomain !== 'string') {
    return false;
  }

  const normalizedSlug = subdomain.toLowerCase().trim();

  // Check format first
  if (!/^[a-z0-9-]+$/.test(normalizedSlug)) return false;
  if (normalizedSlug.length < 3 || normalizedSlug.length > 30) return false;
  if (normalizedSlug.startsWith('-') || normalizedSlug.endsWith('-')) return false;
  if (normalizedSlug.includes('--')) return false;

  // Check infrastructure reserved words
  if (INFRASTRUCTURE_RESERVED.has(normalizedSlug)) return false;

  // Check patterns
  for (const pattern of BLOCK_PATTERNS) {
    if (pattern.test(normalizedSlug)) return false;
  }

  return true;
};

export {
  getIsRestaurantSlugValid,
  INFRASTRUCTURE_RESERVED,
  SLUG_ERROR_CODES,
  validateSlugFormat
};
